/** SUNTRONIX ONE — Bulk HSN Auto-Enrichment.
 *  Catalogue Data Quality → HSN Enrichment. Governed, background batch that
 *  fills MISSING HSN codes (never overwrites VERIFIED), with dry-run,
 *  confidence tiers, a review workbench, certify, and rollback.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Sparkles, ShieldCheck, AlertTriangle, Loader2, PlayCircle, FlaskConical,
  Undo2, CheckCircle2, RefreshCw, Gauge,
} from "lucide-react";

const TIER_TONE = {
  HIGH: "bg-emerald-50 border-emerald-200 text-emerald-700",
  MEDIUM: "bg-amber-50 border-amber-200 text-amber-700",
  LOW: "bg-rose-50 border-rose-200 text-rose-700",
};

export default function HsnEnrichment() {
  const [access, setAccess] = useState(null);
  const [cov, setCov] = useState(null);
  const [batch, setBatch] = useState(null);
  const [autoSave, setAutoSave] = useState(true);
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const poll = useRef(null);

  const loadCoverage = useCallback(() => api.get("/hsn-enrichment/coverage").then(({ data }) => setCov(data)).catch(() => {}), []);
  const loadReview = useCallback((bid) => {
    const p = bid ? `?batch_id=${bid}` : "";
    api.get(`/hsn-enrichment/review${p}`).then(({ data }) => setItems(data.items || [])).catch(() => {});
  }, []);
  const loadLatest = useCallback(() => {
    api.get("/hsn-enrichment/batches").then(({ data }) => {
      const b = (data.items || [])[0] || null;
      setBatch(b);
      if (b) loadReview(b.id);
    }).catch(() => {});
  }, [loadReview]);

  useEffect(() => {
    api.get("/hsn-enrichment/access").then(({ data }) => setAccess(data)).catch(() => setAccess({ can_enrich: false }));
    loadCoverage(); loadLatest();
  }, [loadCoverage, loadLatest]);

  // Poll a running batch.
  useEffect(() => {
    if (batch?.status === "RUNNING") {
      poll.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/hsn-enrichment/batches/${batch.id}`);
          setBatch(data);
          if (data.status !== "RUNNING") { loadCoverage(); loadReview(data.id); }
        } catch { /* ignore */ }
      }, 2000);
      return () => clearInterval(poll.current);
    }
  }, [batch?.status, batch?.id, loadCoverage, loadReview]);

  const start = async (mode) => {
    setBusy(true);
    try {
      const { data } = await api.post("/hsn-enrichment/run", { mode, auto_save: autoSave });
      setBatch(data);
      toast.success(mode === "DRY_RUN" ? "Dry run started — no data will be written." : "Enrichment started in the background.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
    finally { setBusy(false); }
  };

  const accept = async (verify, ids) => {
    const item_ids = ids || Object.keys(sel).filter((k) => sel[k]);
    if (!item_ids.length) return toast.error("Select at least one product");
    try {
      const { data } = await api.post("/hsn-enrichment/review/accept", { item_ids, verify });
      toast.success(`${verify ? "Verified" : "Accepted"} ${data.applied} product(s)`);
      setSel({}); loadCoverage(); loadReview(batch?.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply"); }
  };

  const rollback = async () => {
    if (!batch || !window.confirm(`Roll back all HSN codes populated by ${batch.id}? (Verified & manually-changed codes are kept.)`)) return;
    try {
      const { data } = await api.post(`/hsn-enrichment/batches/${batch.id}/rollback`);
      toast.success(`Reverted ${data.reverted} product(s)`);
      loadCoverage(); loadLatest();
    } catch (e) { toast.error(e.response?.data?.detail || "Rollback failed"); }
  };

  if (access && !access.can_enrich) {
    return <div className="p-6"><div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-[13px] text-slate-500" data-testid="hsn-enrich-denied">HSN Enrichment requires a Product/Technology admin role.</div></div>;
  }

  const running = batch?.status === "RUNNING";

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-5" data-testid="hsn-enrichment-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-violet-600 grid place-items-center text-white"><Sparkles className="h-5 w-5" /></div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>HSN Enrichment</h1>
          <p className="text-[13px] text-slate-500">Auto-fill missing HSN codes across the catalogue — governed, reviewed, reversible.</p>
        </div>
      </div>

      {/* Coverage cards */}
      {cov && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="hsn-coverage">
          <Cov label="Coverage" value={`${cov.coverage_pct}%`} tone="text-slate-900" icon={Gauge} testid="cov-pct" />
          <Cov label="Verified" value={cov.verified} tone="text-emerald-700" testid="cov-verified" />
          <Cov label="Auto-assigned" value={cov.auto_assigned} tone="text-sky-700" testid="cov-auto" />
          <Cov label="Review required" value={cov.review_required} tone="text-amber-700" testid="cov-review" />
          <Cov label="Missing" value={cov.missing} tone="text-rose-700" testid="cov-missing" />
          <Cov label="HSN/GST conflict" value={cov.hsn_gst_conflict} tone="text-fuchsia-700" testid="cov-gst" />
        </div>
      )}

      {/* Controls */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3" data-testid="hsn-controls">
        <label className="flex items-center gap-2 text-[13px] text-slate-700">
          <input type="checkbox" checked={autoSave} onChange={(e) => setAutoSave(e.target.checked)} data-testid="hsn-autosave" />
          Auto-save HIGH-confidence codes
        </label>
        <div className="flex-1" />
        <Button variant="outline" disabled={busy || running} onClick={() => start("DRY_RUN")} data-testid="hsn-dry-run">
          <FlaskConical className="h-4 w-4 mr-1.5" />Dry run
        </Button>
        <Button disabled={busy || running} onClick={() => start("LIVE")} data-testid="hsn-enrich-run" className="bg-violet-600 hover:bg-violet-700">
          {running ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
          ✨ Enrich missing HSN codes
        </Button>
        <Button variant="outline" onClick={() => { loadCoverage(); loadLatest(); }} data-testid="hsn-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {/* Batch progress */}
      {batch && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="hsn-batch">
          <div className="flex items-center justify-between">
            <div className="text-[13px]">
              <span className="font-mono font-semibold">{batch.id}</span>
              <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50">{batch.mode}</span>
              <span className={"ml-2 text-[11px] font-semibold " + (batch.status === "RUNNING" ? "text-amber-600" : batch.status === "DONE" ? "text-emerald-600" : "text-rose-600")} data-testid="hsn-batch-status">
                {batch.status === "RUNNING" ? "HSN ENRICHMENT RUNNING" : batch.status}
              </span>
            </div>
            {batch.status === "DONE" && !batch.rolled_back && batch.mode === "LIVE" && (
              <Button size="sm" variant="outline" onClick={rollback} data-testid="hsn-rollback" className="text-rose-600 border-rose-200 hover:bg-rose-50">
                <Undo2 className="h-3.5 w-3.5 mr-1" />Rollback batch
              </Button>
            )}
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-3 text-center">
            <Mini label="Processed" v={`${batch.processed}/${batch.total}`} />
            <Mini label="Matched" v={batch.matched} tone="text-emerald-700" />
            <Mini label="Review" v={batch.review} tone="text-amber-700" />
            <Mini label="Unresolved" v={batch.unresolved} tone="text-slate-500" />
            <Mini label="Failed" v={batch.failed} tone="text-rose-700" />
            <Mini label="Mode" v={batch.mode === "DRY_RUN" ? "Dry" : "Live"} />
          </div>
          {running && <div className="text-[11px] text-slate-400 mt-2">You can leave this screen — the batch runs in the background.</div>}
        </div>
      )}

      {/* Review workbench */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[13px] font-semibold text-slate-700">Review required <span className="text-slate-400 font-normal">({items.length})</span></h3>
          {items.length > 0 && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => accept(false)} data-testid="hsn-bulk-accept"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Accept selected</Button>
              {access?.can_verify && <Button size="sm" onClick={() => accept(true)} data-testid="hsn-bulk-verify" className="bg-emerald-700 hover:bg-emerald-800"><ShieldCheck className="h-3.5 w-3.5 mr-1" />Verify selected</Button>}
            </div>
          )}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="hsn-review-table">
            <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
              <th className="px-2 py-2 w-8"></th>
              {["Product", "SKU/OEM", "Category", "Current", "Suggested", "Conf.", "Reason", ""].map((h, i) => <th key={i} className="px-3 py-2 font-semibold">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">Nothing awaiting review. Run enrichment to populate suggestions.</td></tr>}
              {items.map((it) => (
                <tr key={it.id} className="hover:bg-slate-50" data-testid={`hsn-review-row-${it.product_id}`}>
                  <td className="px-2 py-2"><input type="checkbox" checked={!!sel[it.id]} onChange={(e) => setSel((s) => ({ ...s, [it.id]: e.target.checked }))} data-testid={`hsn-sel-${it.product_id}`} /></td>
                  <td className="px-3 py-2 text-slate-800 max-w-[220px] truncate">{it.product_name}{it.gst_conflict && <span className="ml-1 text-[9px] font-semibold text-fuchsia-600" title="HSN/GST inconsistency">GST?</span>}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{it.sku_oem || "—"}</td>
                  <td className="px-3 py-2 text-slate-500">{it.category || "—"}</td>
                  <td className="px-3 py-2 font-mono text-slate-400">{it.old_hsn || "—"}</td>
                  <td className="px-3 py-2 font-mono font-semibold text-slate-900">{it.suggested_hsn || <span className="text-rose-500 font-sans font-normal">UNRESOLVED</span>}</td>
                  <td className="px-3 py-2"><span className={"text-[10px] px-1.5 py-0.5 rounded border " + (TIER_TONE[it.tier] || "border-slate-200")}>{it.tier} {it.confidence != null ? `${Math.round(it.confidence)}%` : ""}</span></td>
                  <td className="px-3 py-2 text-slate-500 max-w-[240px] truncate" title={it.reason}>{it.reason}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {it.suggested_hsn && <button onClick={() => accept(false, [it.id])} className="text-[11px] text-indigo-600 hover:text-indigo-800 font-medium" data-testid={`hsn-accept-${it.product_id}`}>Accept</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Cov({ label, value, tone = "text-slate-900", icon: Icon, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="text-[10px] font-medium text-slate-500 flex items-center gap-1">{Icon && <Icon className="h-3 w-3" />}{label}</div>
      <div className={"text-[19px] font-bold mt-0.5 tabular-nums " + tone}>{value}</div>
    </div>
  );
}
function Mini({ label, v, tone = "text-slate-900" }) {
  return <div className="rounded-lg border border-slate-100 bg-slate-50 p-2"><div className="text-[9px] uppercase text-slate-400">{label}</div><div className={"text-[15px] font-bold tabular-nums " + tone}>{v}</div></div>;
}
